import type { AnalyticsSession, DeviceMetadata, ResolvedConfig, TrackedEvent } from "../types";

/**
 * ⚠️ THE ONLY FILE THAT KNOWS THE BACKEND CONTRACT.
 *
 * Paths, headers and payload shape all live here. Nothing else in the SDK — not track(),
 * not the session manager, not the queue — knows what the wire looks like, so a contract
 * change is a change to this file alone.
 *
 * All three routes are authenticated by the gateway's AnalyticsIngestKeyPolicy, which
 * compares the X-Analytics-Key header. They answer 201, 200 and 202 respectively.
 */

const BASE_PATH = "/api/businessanalytics";
const SESSIONS_PATH = `${BASE_PATH}/sessions`;
const EVENTS_BATCH_PATH = `${BASE_PATH}/events/batch`;

const API_KEY_HEADER = "X-Analytics-Key";

export interface WireRequest {
	url: string;
	init: RequestInit;
}

function endpointUrl(config: ResolvedConfig, path: string): string {
	return `${config.endpoint.replace(/\/+$/, "")}${path}`;
}

function requestInit(config: ResolvedConfig, method: string, body: unknown, keepalive: boolean): RequestInit {
	return {
		method,
		headers: {
			"Content-Type": "application/json",
			[API_KEY_HEADER]: config.apiKey,
		},
		body: JSON.stringify(body),
		keepalive,
	};
}

export function sessionRequest(config: ResolvedConfig, session: AnalyticsSession, device: DeviceMetadata): WireRequest {
	return {
		url: endpointUrl(config, SESSIONS_PATH),
		init: requestInit(
			config,
			"POST",
			{
				utilityName: config.utilityName,
				sessionId: session.sessionId,
				startedAt: session.startedAt,
				deviceId: device.deviceId,
				platform: device.platform,
				osName: device.osName,
				osVersion: device.osVersion,
				deviceBrand: device.deviceBrand,
				deviceManufacturer: device.deviceManufacturer,
				deviceModel: device.deviceModel,
				language: device.language,
				appVersion: config.appVersion,
				// A browser cannot read its own public IP; the backend stamps it from the request.
				ipAddress: null,
			},
			false
		),
	};
}

/**
 * Closes a session. Every field on this endpoint overwrites what is stored, so sending
 * `endedAt` again on a session that resumed simply corrects it.
 *
 * `userId` is deliberately not sent: the backend attaches it to the session from the first
 * event that carries one, so patching it here would be a redundant request racing the POST
 * that creates the session.
 *
 * `utilityName` is not a patched field — the endpoint needs it to resolve the tenant.
 */
export function endSessionRequest(config: ResolvedConfig, sessionId: string, endedAt: string, keepalive = false): WireRequest {
	return {
		url: endpointUrl(config, `${SESSIONS_PATH}/${sessionId}`),
		init: requestInit(config, "PATCH", { utilityName: config.utilityName, endedAt }, keepalive),
	};
}

export function eventsRequest(config: ResolvedConfig, events: TrackedEvent[], keepalive = false): WireRequest {
	return {
		url: endpointUrl(config, EVENTS_BATCH_PATH),
		init: requestInit(
			config,
			"POST",
			{
				// The tenant travels once, at the top level — not on each event.
				utilityName: config.utilityName,
				events: events.map(event => ({
					sessionId: event.sessionId,
					userId: event.userId,
					insertId: event.insertId,
					flowCode: event.flowName,
					code: event.eventName,
					occurredAt: event.timestamp,
					// ⚠️ The contract types this as a string, not an object.
					payload: JSON.stringify(event.customFields),
				})),
			},
			keepalive
		),
	};
}
